import { PickerButtonIndicator } from "@/components/picker-with-sheet"
import { EnumsOf } from "@/lib/utils"
import {
  FormSchema,
  MachineFormData,
  MachineFormSchema,
} from "@/packaging/lib/form"
import { DepartmentSchema } from "@/packaging/lib/schema"
import { PackagingHistory } from "@/services/packaging"
import { StateCreator } from "@/store/utils"
import { LabelValue } from "@/types/common"

export interface PackagingState {
  shift: {
    processId: string
    shiftId: string
  }
  histories: DeptHistory[]
  submissions: FormSubmission[]
  submitted: Record<string, boolean>
  totalLeakage: Record<string, number>
  allSchemas: DepartmentSchema[]
  allDepartments: LabelValue[]
  allMachines: Record<string, MachineOption[]>
  currSheet: PackagingSheets
  formSchemas: FormSchema[]
  forms: MachineFormData[]
  departments: LabelValue[]
  currDeptId: string
  currMachineId: string
  indicators: Record<string, PickerButtonIndicator>
}

export const PackagingSheets = {
  None: "None",
  AddDept: "AddDept",
  ConfirmEndShift: "ConfirmEndShift",
  EndShiftError: "EndShiftError",
  EndShiftLoading: "EndShiftLoading",
  DiscardShift: "DiscardShift",
  ConfirmSubmit: "ConfirmSubmit",
  ConfirmDeleteLeakage: "ConfirmDeleteLeakage",
  ConfirmDeleteProduct: "ConfirmDeleteProduct",
} as const
export type PackagingSheets = EnumsOf<typeof PackagingSheets>

export interface MachineOption extends LabelValue { }
export interface DeptHistory {
  id: string
  sheets: PackagingHistory[]
}

export function initialState(): PackagingState {
  return {
    shift: {
      processId: "",
      shiftId: "",
    },
    submissions: [],
    submitted: {},
    totalLeakage: {},
    allSchemas: [],
    histories: [],
    allDepartments: [],
    allMachines: {},
    currSheet: PackagingSheets.None,
    formSchemas: [],
    forms: [],
    departments: [],
    currDeptId: "",
    currMachineId: "",
    indicators: {},
  }
}

export interface PackagingSlice {
  packaging: PackagingState & {
    setAllSchemas(schemas: DepartmentSchema[]): void
    getMachines(deptId: string): MachineOption[]
    setCurrSheet(sheet: PackagingSheets): void
    setDepts(depts: LabelValue[]): void
    addDeptForm(form: FormSchema): void
    addDeptForms(forms: FormSchema[]): void
    getDeptSchema(deptId: string): DepartmentSchema | undefined
    setCurrDeptId(deptId: string): void
    setCurrMachineId(machineId: string): void
    setMachineFormSchema(deptId: string, schema: MachineFormSchema): void
    setMachineFormData(deptId: string, data: MachineFormData): void
    removeProductFormData(
      deptId: string,
      machineId: string,
      productId: string
    ): void
    getFormData(deptId: string, machineId: string): MachineFormData | undefined
    addSubmission(submission: FormSubmission): void
    // setSubmitted(key: string): void
    // getIsSubmitted(key: string): boolean
    getNextSubmission(): FormSubmission | undefined
    dequeOldestSubmission(): void
    startShift(details: PackagingState["shift"]): void
    clearShift(): void
    setTotalLeakage(productId: string, leakage: number): void
    setIndicators(indicators: Record<string, PickerButtonIndicator>): void

    addHistory(history: DeptHistory): void
    getHistory(deptId: string): void

    setProcessDetails(details: PackagingState["shift"]): void

    removePendingSheets(): void
  }
}

// function toSubmittedStatusKey(submission: FormSubmission) {
//   return `${submission.deptId}.${submission.productId}`
// }

export interface FormSubmission {
  deptName: string
  productName: string
  sectionName: string
  machineName: string
  deptId: string
  machineId: string
  productId: string
  sectionId: string
}

function actionName(action: string) {
  return `packaging/${action}`
}

const createPackagingSlice: StateCreator<PackagingSlice> = (set, get) => ({
  packaging: {
    ...initialState(),
    setAllSchemas(schemas) {
      set(
        (state) => {
          state.packaging.allSchemas = schemas

          const departments: LabelValue[] = []
          const machines: Record<string, MachineOption[]> = {}
          for (const dept of schemas) {
            departments.push({
              id: dept.id,
              label: dept.name,
              value: dept.id,
            })

            for (const machine of dept.machines) {
              if (!machines[dept.id]) {
                machines[dept.id] = []
              }

              machines[dept.id].push({
                id: machine.id,
                label: machine.name,
                value: machine.id,
              })
            }
          }

          state.packaging.allDepartments = departments
          state.packaging.allMachines = machines

          return state
        },
        undefined,
        actionName("setSchemas")
      )
    },
    getMachines(deptId) {
      const state = get().packaging

      if (state.allMachines[deptId]) {
        return state.allMachines[deptId]
      }

      return []
    },
    setCurrSheet(sheet) {
      set(
        (state) => {
          state.packaging.currSheet = sheet

          return state
        },
        undefined,
        actionName("setCurrSheet")
      )
    },
    setDepts(depts) {
      set(
        (state) => {
          state.packaging.departments = depts

          return state
        },
        undefined,
        actionName("setDepts")
      )
    },
    addDeptForm(form) {
      set(
        (state) => {
          state.packaging.formSchemas.push(form)

          return state
        },
        undefined,
        actionName("addDeptForm")
      )
    },
    addDeptForms(forms) {
      set(
        (state) => {
          state.packaging.formSchemas.push(...forms)

          return state
        },
        undefined,
        actionName("addDeptForms")
      )
    },
    getDeptSchema(deptId) {
      const state = get().packaging

      return state.allSchemas.find((schema) => schema.id === deptId)
    },
    setCurrDeptId(deptId) {
      set(
        (state) => {
          state.packaging.currDeptId = deptId

          const dept = state.packaging.formSchemas.find(
            (item) => item.id === deptId
          )

          if (dept) {
            state.packaging.currMachineId = dept.machines.at(0)?.id || ""
          }

          return state
        },
        undefined,
        actionName("setCurrDeptId")
      )
    },
    setCurrMachineId(machineId) {
      set(
        (state) => {
          state.packaging.currMachineId = machineId

          return state
        },
        undefined,
        actionName("setCurrMachineId")
      )
    },
    setMachineFormSchema(deptId, schema) {
      set(
        (state) => {
          const form = state.packaging.formSchemas.find(
            (item) => item.id === deptId
          )

          if (!form) {
            return
          }

          form.machines = form.machines.map((machine) => {
            if (machine.id === schema.id) {
              return schema
            }

            return machine
          })

          return state
        },
        undefined,
        actionName("setMachineFormSchema")
      )
    },
    setMachineFormData(deptId, data) {
      set(
        (state) => {
          const formIdx = state.packaging.forms.findIndex(
            (item) => item.deptId === deptId && item.id === data.id
          )

          if (formIdx < 0) {
            state.packaging.forms.push(data)
          } else {
            state.packaging.forms[formIdx] = data
          }

          return state
        },
        undefined,
        actionName("setMachineFormData")
      )
    },
    removeProductFormData(deptId, machineId, productId) {
      set(
        (state) => {
          const form = state.packaging.forms.find(
            (item) => item.deptId === deptId && item.id === machineId
          )

          if (!form) {
            return
          }

          form.products = form.products.filter(
            (product) => product.id !== productId
          )

          return state
        },
        undefined,
        actionName("removeProductFormData")
      )
    },
    getFormData(deptId, machineId) {
      const state = get().packaging

      return state.forms.find(
        (item) => item.deptId === deptId && item.id === machineId
      )
    },
    startShift(details) {
      set(
        (state) => {
          state.packaging.shift = details

          return state
        },
        undefined,
        actionName("startShift")
      )
    },
    clearShift() {
      set(
        (state) => {
          state.session.session.processes =
            state.session.session.processes.filter(
              (proc) => proc.processId !== state.packaging.shift.processId
            )

          state.packaging = {
            ...state.packaging,
            ...initialState(),
            allDepartments: state.packaging.allDepartments,
            allMachines: state.packaging.allMachines,
            allSchemas: state.packaging.allSchemas,
          }

          return state
        },
        undefined,
        actionName("clearShift")
      )
    },
    addSubmission(submission) {
      set(
        (state) => {
          state.packaging.submissions.push(submission)

          return state
        },
        undefined,
        actionName("addSubmission")
      )
    },
    getNextSubmission() {
      const state = get().packaging

      return state.submissions.at(0)
    },
    dequeOldestSubmission() {
      set(
        (state) => {
          state.packaging.submissions.shift()

          return state
        },
        undefined,
        actionName("dequeOldestSubmission")
      )
    },
    setTotalLeakage(productId, leakage) {
      set(
        (state) => {
          state.packaging.totalLeakage[productId] = leakage

          return state
        },
        undefined,
        actionName("setTotalLeakage")
      )
    },
    setIndicators(indicators) {
      set(
        (state) => {
          state.packaging.indicators = indicators

          return state
        },
        undefined,
        actionName("setIndicators")
      )
    },
    addHistory(history) {
      set(
        (state) => {
          state.packaging.histories.push(history)

          return state
        },
        undefined,
        actionName("addHistory")
      )
    },
    getHistory(deptId) {
      const state = get().packaging

      return state.histories.find((history) => history.id === deptId)
    },
    setProcessDetails(details) {
      set(
        (state) => {
          state.packaging.shift = details

          return state
        },
        undefined,
        actionName("setProcessDetails")
      )
    },
    removePendingSheets() {
      set(
        (state) => {
          const filled = new Set(
            Object.entries(state.packaging.indicators)
              .filter(([__key, indicator]) => {
                if (indicator === PickerButtonIndicator.Success) {
                  return true
                }

                return false
              })
              .map((item) => item.at(0))
          )

          state.packaging.formSchemas = state.packaging.formSchemas.map(
            (schema) => {
              schema.machines = schema.machines.filter((machine) => {
                const key = `${schema.id}.${machine.id}`

                return filled.has(key)
              })

              return schema
            }
          )

          state.packaging.formSchemas = state.packaging.formSchemas.filter(
            (schema) => schema.machines.length
          )

          return state
        },
        undefined,
        actionName("removePendingSheets")
      )
    },
  },
})

export default createPackagingSlice
